import React from "react";
import { Quote } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Testimonial — Big brutalist pull-quote lifted from the FAQ (Q6).
 * Anonymous initials-avatar; single strong quote for now.
 * Placed between Pricing and FAQ to reinforce decision-making.
 */
export const Testimonial = () => {
  const { t } = useLanguage();

  return (
    <section
      id="asiakasarvio"
      data-testid="testimonial-section"
      className="relative py-20 md:py-28 bg-black border-y-2 border-[#1f1f1f] overflow-hidden"
    >
      {/* Left neon vertical stripe */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1 bg-[#FFEA00]"
      />
      {/* Background grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,234,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,234,0,0.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 md:px-10">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 border-2 border-[#FFEA00] bg-black px-3 py-1.5 mb-8">
          <span className="w-1.5 h-1.5 bg-[#FFEA00] rounded-full pulse-dot" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFEA00]">
            {t("testimonial.eyebrow")}
          </span>
        </div>

        {/* Big quote block */}
        <div className="relative">
          {/* Giant quote mark */}
          <Quote
            aria-hidden="true"
            className="absolute -top-4 -left-3 md:-top-8 md:-left-6 w-16 h-16 md:w-24 md:h-24 text-[#FFEA00] fill-[#FFEA00]"
            strokeWidth={0}
          />

          <blockquote className="relative pl-8 md:pl-16">
            <p className="font-display font-bold text-white text-2xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
              {t("testimonial.quote_1")}{" "}
              <span className="text-[#FFEA00]">{t("testimonial.quote_accent")}</span>{" "}
              {t("testimonial.quote_2")}
            </p>
          </blockquote>
        </div>

        {/* Attribution */}
        <div className="mt-10 flex items-center gap-4 pl-8 md:pl-16">
          {/* Initials avatar */}
          <div
            aria-hidden="true"
            data-testid="testimonial-avatar"
            className="shrink-0 w-14 h-14 border-2 border-[#FFEA00] bg-black flex items-center justify-center shadow-[3px_3px_0_0_rgba(255,234,0,0.35)]"
          >
            <span className="font-display font-bold text-[#FFEA00] text-lg tracking-tight">
              {t("testimonial.avatar_initials")}
            </span>
          </div>

          <div className="min-w-0">
            <div className="font-display font-bold text-white text-sm md:text-base uppercase tracking-wide">
              {t("testimonial.author")}
            </div>
            <div className="font-mono text-[11px] md:text-xs text-[#A3A3A3] tracking-wide uppercase mt-0.5">
              {t("testimonial.context")}
            </div>
          </div>
        </div>

        {/* Bottom hazard stripe */}
        <div
          aria-hidden="true"
          className="mt-12 h-1.5 hazard-stripes-anim"
        />
      </div>
    </section>
  );
};
